import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const DataImport = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/import/templates', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = activeTab === 'clients' ? '/api/import/clients' : '/api/import/appointments';
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
    } catch (error) {
      setResult({
        message: 'Import failed',
        error: error.response?.data?.message || error.message,
        errors: error.response?.data?.errors || []
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = (type) => {
    if (!templates) return;

    const template = templates[type];
    const headers = template.headers.join(',');
    const example = Object.values(template.example).join(',');

    const csvContent = `${headers}\n${example}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = template.filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Data Import</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'clients'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Import Clients
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'appointments'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Import Appointments
          </button>
        </div>

        {templates && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
            <p className="text-sm text-blue-800 mb-3">
              Download the template, fill it with your data, and upload it here.
              The file must be in CSV or Excel format.
            </p>
            <button
              onClick={() => downloadTemplate(activeTab)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              Download {activeTab === 'clients' ? 'Clients' : 'Appointments'} Template
            </button>

            <div className="mt-4">
              <h4 className="font-medium text-blue-900 mb-2">Field Requirements:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                {activeTab === 'clients' ? (
                  <>
                    <li><strong>first_name, last_name:</strong> Required</li>
                    <li><strong>gender:</strong> male, female, or other</li>
                    <li><strong>date_of_birth:</strong> Format: YYYY-MM-DD</li>
                    <li><strong>weight:</strong> In kilograms (kg)</li>
                    <li><strong>height:</strong> In centimeters (cm)</li>
                    <li><strong>medical_conditions, allergies, nutrition_goals:</strong> Comma-separated values</li>
                  </>
                ) : (
                  <>
                    <li><strong>client_phone or client_email:</strong> Required (must match existing client)</li>
                    <li><strong>appointment_date:</strong> Format: YYYY-MM-DD</li>
                    <li><strong>appointment_time:</strong> Format: HH:MM (24-hour)</li>
                    <li><strong>type:</strong> initial_consultation, follow_up, program_review, or assessment</li>
                    <li><strong>status:</strong> scheduled, confirmed, completed, cancelled, or rescheduled</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}

        <form onSubmit={handleImport} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File (CSV or Excel)
            </label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !file}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'Importing...' : 'Import Data'}
          </button>
        </form>

        {result && (
          <div className={`mt-6 p-4 rounded-lg ${result.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
            <h3 className="font-semibold mb-2">{result.message || 'Import Results'}</h3>

            {result.summary && (
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">{result.summary.total}</div>
                  <div className="text-sm text-gray-600">Total Records</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{result.summary.success}</div>
                  <div className="text-sm text-gray-600">Successfully Imported</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{result.summary.failed}</div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
              </div>
            )}

            {result.errors && result.errors.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-red-800 mb-2">Errors:</h4>
                <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((error, index) => (
                    <li key={index} className="list-disc list-inside">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.imported && result.imported.length > 0 && (
              <div className="mt-4">
                <h4 className="font-medium text-green-800 mb-2">Successfully Imported:</h4>
                <ul className="text-sm text-green-700 space-y-1 max-h-40 overflow-y-auto">
                  {result.imported.map((item, index) => (
                    <li key={index} className="list-disc list-inside">
                      {item.name || item.id}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-900 mb-2">Important Notes:</h3>
        <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
          <li>Maximum file size: 10MB</li>
          <li>For appointments, clients must already exist in the system</li>
          <li>Duplicate entries will be rejected by the database</li>
          <li>All imports are logged in the audit trail</li>
          <li>Review errors carefully and fix the data before re-importing</li>
        </ul>
      </div>
    </div>
  );
};

export default DataImport;